import React, { ReactElement, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";

import LoginModal from "@components/UserModals/LoginModal";
import { IUser } from "@typed/User";
import { useGacUser } from "@services/gacUser/useGacUser";
import { initUserService, setGacUser } from "@services/gacUser/gacUserService";
import { logout as cognitoLogout } from "@contexts/AuthContext/functions/logout";
import toast from "react-hot-toast";
import { getAuthToken } from "./functions/getAuthToken";
import { authBus, AuthEvent } from "./AuthEvents";

type AuthContextType = {
  logout: () => Promise<void>;
  isloggedIn: boolean;
  email?: string;

  gacUser: IUser | null;
  setGacUser: (user: IUser | null) => void;
  getAuthToken: () => Promise<string | null>;

  // getUserRoles: () => Promise<string[] | null>;

  ShowLoginModal: (tab: "login" | "new-account" | "forgot-password") => void;
  showingLoginModal: boolean;
};

const AuthContext = createContext<AuthContextType>({
  logout: async () => Promise.resolve(),
  isloggedIn: false,
  email: undefined,

  gacUser: null,
  setGacUser: (_user: IUser | null) => null,
  getAuthToken: async () => null,

  ShowLoginModal: (_tab) => null,
  showingLoginModal: false,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactElement | ReactElement[] }) => {
  const [loginOpen, setLoginOpen] = useState(false);
  const [activeLoginModalTab, setActiveLoginModalTab] = useState<"login" | "new-account" | "forgot-password">("login");

  const gacUser = useGacUser();

  const value = useMemo(
    () => ({
      logout: async () => authBus.emitTransition("logout"),
      isloggedIn: !!gacUser,
      email: gacUser?.email ?? undefined,

      gacUser,
      setGacUser: (user: IUser | null) => {
        setGacUser(user);
      },
      getAuthToken: async () => getAuthToken(),

      // eslint-disable-next-line default-param-last
      ShowLoginModal: async (tab: "login" | "new-account" | "forgot-password" = "login") => {
        if (typeof window !== "undefined" && tab === "new-account") {
          (window as any)?.plausible?.("Register-Modal-Open");
        }
        // set active tab in login modal
        setActiveLoginModalTab(tab);

        // open the modal
        setLoginOpen(true);
      },
      showingLoginModal: loginOpen,
    }),
    [loginOpen, activeLoginModalTab, gacUser],
  );

  const showLoginModalRef = useRef(value.ShowLoginModal);

  useEffect(() => {
    showLoginModalRef.current = value.ShowLoginModal;
  }, [value.ShowLoginModal]);

  useEffect(() => {
    console.log("AuthContextProvider mounted");
    initUserService();

    if (typeof window === "undefined") return undefined;

    // wire up simple auth event listener
    const handleAuthEvent = async (e: AuthEvent) => {
      if (e.type !== "transition") {
        return;
      }

      const { state } = e;
      if (state === "login" || state === "new-account" || state === "forgot-password") {
        showLoginModalRef.current(state);
        return;
      }

      if (state === "logout") {
        toast.loading("Logging out ...", { id: "auth-status" });
        await cognitoLogout();
        toast.dismiss("auth-status");
      }
    };

    const unsubscribe: () => void = authBus.subscribe(handleAuthEvent);
    return () => {
      console.log("AuchcontextProvider... Unsubscribing from authBus");
      unsubscribe();
    };
  }, []);

  return (
    <AuthContext.Provider value={value}>
      {children}

      <LoginModal isOpen={loginOpen} setIsOpen={setLoginOpen} focusTab={activeLoginModalTab} setFocusTab={setActiveLoginModalTab} />
    </AuthContext.Provider>
  );
};
